import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "../../components/ui/button";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import { methodologyService, HuntingToolkitGroup } from "../../lib/firebase";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Copy, Check, Shield, Search, X, List, Grid } from "lucide-react";
import { toast } from "../../components/ui/use-toast";
import { Input } from "../../components/ui/input";
import NavBar from '../../components/NavBar';
import { useUserName } from '../../lib/useUserName';

export const HuntingToolkitPage = (): JSX.Element => {
  const navigate = useNavigate();
  const userName = useUserName();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [groups, setGroups] = useState<HuntingToolkitGroup[]>([]);
  const [copiedCommand, setCopiedCommand] = useState<string | null>(null);
  const [targetDomain, setTargetDomain] = useState(() => {
    return localStorage.getItem('targetDomain') || 'example.com';
  });
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('');
  const [viewMode, setViewMode] = useState<'list' | 'cards'>('list');
  const auth = getAuth();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!user) {
        navigate('/signin');
      } else {
        // The useUserName hook handles setting the user name in localStorage
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, [navigate, auth]);

  // Load toolkits on component mount
  useEffect(() => {
    loadToolkits();
  }, []);

  useEffect(() => {
    localStorage.setItem('targetDomain', targetDomain);
  }, [targetDomain]);

  const loadToolkits = async () => {
    try {
      setLoading(true);
      const groupsData = await methodologyService.getHuntingToolkitGroups();
      setGroups(groupsData);
    } catch (error) {
      console.error("Failed to load hunting toolkit groups:", error);
      setError("Failed to load toolkits");
      toast({
        title: "Error",
        description: "Failed to load toolkits",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const replaceDomain = (command: string) => {
    return command.replace(/<target_domain>/g, targetDomain || 'example.com');
  };

  // Filter groups and sections based on search query and selected group
  const filteredGroups = groups.filter(group => {
    // Filter by selected group
    if (selectedGroup && group.title !== selectedGroup) {
      return false;
    }

    // If no search query, return all groups
    if (!searchQuery.trim()) {
      return true;
    }

    const query = searchQuery.toLowerCase();
    
    // Check if group title or description matches
    if (group.title.toLowerCase().includes(query) || 
        (group.description && group.description.toLowerCase().includes(query))) {
      return true;
    }

    // Check if any section matches
    return group.sections.some(section => 
      section.title.toLowerCase().includes(query) ||
      (section.description && section.description.toLowerCase().includes(query)) ||
      section.command.toLowerCase().includes(query)
    );
  });

  // Filter sections within each group
  const getFilteredSections = (sections: any[]) => {
    if (!searchQuery.trim()) {
      return sections;
    }

    const query = searchQuery.toLowerCase();
    return sections.filter(section => 
      section.title.toLowerCase().includes(query) ||
      (section.description && section.description.toLowerCase().includes(query)) ||
      section.command.toLowerCase().includes(query)
    );
  };

  const handleCopyCommand = async (command: string) => {
    try {
      await navigator.clipboard.writeText(replaceDomain(command));
      setCopiedCommand(command);
      toast({
        title: "Copied!",
        description: "Command copied to clipboard",
      });
      
      // Reset copied state after 2 seconds
      setTimeout(() => {
        setCopiedCommand(null);
      }, 2000);
    } catch (error) {
      console.error("Failed to copy command:", error);
      toast({
        title: "Error",
        description: "Failed to copy command",
        variant: "destructive",
      });
    }
  };

  const handleSignOut = async () => {
    try {
      if (auth) {
        await auth.signOut();
        navigate('/signin');
      }
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const clearFilters = () => {
    setSearchQuery('');
    setSelectedGroup('');
  };

  // Get unique group titles for filter dropdown
  const groupTitles = [...new Set(groups.map(group => group.title))];

  if (loading) {
    return (
      <div className="min-h-screen w-full bg-[#e1dcdc75] flex items-center justify-center">
        <div className="text-black">Loading...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen w-full bg-[#e1dcdc75] flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <Button onClick={() => window.location.reload()}>Retry</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full bg-[#e1dcdc75] relative">
      <NavBar userName={userName} onSignOut={handleSignOut} />

      {/* Main content */}
      <div className="container mx-auto px-4 pt-[160px] pb-12">
        <div className="bg-white rounded-[20px] p-8 shadow-lg">
          {/* Mobile back button */}
          <div className="lg:hidden mb-4">
            <button 
              onClick={() => navigate('/toolkits')}
              className="text-black opacity-60 hover:opacity-100 transition-opacity font-['Space_Grotesk']"
            >
              ← Back to Toolkits
            </button>
          </div>

          {/* Domain Input */}
          <div className="mb-8 flex items-center gap-4">
            <label htmlFor="target-domain" className="font-semibold text-black">Target Domain:</label>
            <Input
              id="target-domain"
              value={targetDomain}
              onChange={e => setTargetDomain(e.target.value)}
              placeholder="Enter target domain (e.g. example.com)"
              className="max-w-xs"
            />
          </div>

          {/* Search and Filters */}
          <div className="mb-8">
            <div className="flex flex-wrap gap-4 items-center justify-between">
              <div className="flex flex-wrap gap-4 items-center">
                <div className="flex items-center gap-2 flex-1 min-w-[200px]">
                  <Search className="w-4 h-4 text-gray-400" />
                  <Input
                    placeholder="Search tools, commands, descriptions..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="flex-1"
                  />
                </div>
                <select
                  value={selectedGroup}
                  onChange={(e) => setSelectedGroup(e.target.value)}
                  className="border rounded px-3 py-2 text-sm min-w-[150px]"
                >
                  <option value="">All Groups</option>
                  {groupTitles.map(title => (
                    <option key={title} value={title}>{title}</option>
                  ))}
                </select>
                {(searchQuery || selectedGroup) && (
                  <Button
                    variant="outline"
                    onClick={clearFilters}
                    size="sm"
                    className="flex items-center gap-2"
                  >
                    <X className="w-4 h-4" />
                    Clear Filters
                  </Button>
                )}
              </div>
              
              {/* View Toggle */}
              <div className="flex items-center gap-2">
                <Button
                  variant={viewMode === 'list' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setViewMode('list')}
                  className="flex items-center gap-2"
                >
                  <List className="w-4 h-4" />
                  List
                </Button>
                <Button
                  variant={viewMode === 'cards' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setViewMode('cards')}
                  className="flex items-center gap-2"
                >
                  <Grid className="w-4 h-4" />
                  Cards
                </Button>
              </div>
            </div>
            {(searchQuery || selectedGroup) && (
              <div className="mt-2 text-sm text-gray-600">
                {filteredGroups.length} group{filteredGroups.length !== 1 ? 's' : ''} found
                {searchQuery && ` for "${searchQuery}"`}
                {selectedGroup && ` in "${selectedGroup}"`}
              </div>
            )}
          </div>

          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-[#daed6e] rounded-lg flex items-center justify-center">
                <Shield className="h-6 w-6 text-black" />
              </div>
              <div>
                <h1 className="font-['Space_Grotesk'] font-bold text-2xl text-black mb-2">
                  Hunting Toolkit
                </h1>
                <p className="font-['Space_Grotesk'] text-sm text-black opacity-80">
                  Curated cybersecurity tools and commands for your penetration testing engagements
                </p>
              </div>
            </div>
          </div>

          {/* Toolkits List/Cards */}
          {viewMode === 'list' ? (
            <div className="space-y-6">
              {filteredGroups.map((group) => {
                const filteredSections = getFilteredSections(group.sections);
                return (
                  <Card key={group.id} className="border-2 border-[#E7E7E7]">
                    <CardHeader>
                      <div className="flex items-center justify-between">
                        <div>
                          <CardTitle className="text-xl text-black">{group.title}</CardTitle>
                          {group.description && (
                            <p className="text-sm text-gray-600 mt-1">{group.description}</p>
                          )}
                        </div>
                        <Badge variant="outline" className="text-sm">
                          {filteredSections.length} {filteredSections.length === 1 ? 'tool' : 'tools'}
                          {filteredSections.length !== group.sections.length && (
                            <span className="ml-1 text-gray-500">
                              (of {group.sections.length})
                            </span>
                          )}
                        </Badge>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {filteredSections.map((section) => (
                          <div key={section.id} className="border rounded-lg p-4 bg-gray-50">
                            <div className="flex items-start justify-between mb-3">
                              <div className="flex-1">
                                <h4 className="font-semibold text-black mb-1">{section.title}</h4>
                                {section.description && (
                                  <p className="text-sm text-gray-600">{section.description}</p>
                                )}
                              </div>
                              <Button
                                onClick={() => handleCopyCommand(section.command)}
                                size="sm"
                                variant="outline"
                                className="ml-4 flex-shrink-0"
                              >
                                {copiedCommand === section.command ? (
                                  <Check size={16} className="mr-1 text-green-600" />
                                ) : (
                                  <Copy size={16} className="mr-1" />
                                )}
                                {copiedCommand === section.command ? 'Copied!' : 'Copy'}
                              </Button>
                            </div>
                            <div className="bg-black text-green-400 p-3 rounded text-sm font-mono overflow-x-auto">
                              <code>{replaceDomain(section.command)}</code>
                            </div>
                          </div>
                        ))}
                        {filteredSections.length === 0 && (
                          <div className="text-center py-4 text-gray-500">
                            No tools found matching your search criteria.
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredGroups.map((group) => {
                const filteredSections = getFilteredSections(group.sections);
                return (
                  <Card key={group.id} className="border-2 border-[#E7E7E7] flex flex-col h-full">
                    <CardHeader className="flex-shrink-0">
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <CardTitle className="text-lg text-black mb-2 line-clamp-2">{group.title}</CardTitle>
                          {group.description && (
                            <p className="text-sm text-gray-600 mb-3 line-clamp-3">{group.description}</p>
                          )}
                          <Badge variant="outline" className="text-xs">
                            {filteredSections.length} {filteredSections.length === 1 ? 'tool' : 'tools'}
                            {filteredSections.length !== group.sections.length && (
                              <span className="ml-1 text-gray-500">
                                (of {group.sections.length})
                              </span>
                            )}
                          </Badge>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent className="flex-1 flex flex-col">
                      <div className="space-y-3 flex-1">
                        {filteredSections.slice(0, 3).map((section) => (
                          <div key={section.id} className="border rounded p-3 bg-gray-50 flex-shrink-0">
                            <div className="flex items-start justify-between mb-2">
                              <div className="flex-1 min-w-0">
                                <h4 className="font-semibold text-sm text-black mb-1 line-clamp-1">{section.title}</h4>
                                {section.description && (
                                  <p className="text-xs text-gray-600 line-clamp-2">{section.description}</p>
                                )}
                              </div>
                              <Button
                                onClick={() => handleCopyCommand(section.command)}
                                size="sm"
                                variant="outline"
                                className="ml-2 flex-shrink-0"
                              >
                                {copiedCommand === section.command ? (
                                  <Check size={14} className="text-green-600" />
                                ) : (
                                  <Copy size={14} />
                                )}
                              </Button>
                            </div>
                            <div className="bg-black text-green-400 p-2 rounded text-xs font-mono overflow-x-auto">
                              <code className="text-xs break-all">{replaceDomain(section.command)}</code>
                            </div>
                          </div>
                        ))}
                        {filteredSections.length > 3 && (
                          <div className="text-center py-2 text-xs text-gray-500 flex-shrink-0">
                            +{filteredSections.length - 3} more tools
                          </div>
                        )}
                        {filteredSections.length === 0 && (
                          <div className="text-center py-4 text-xs text-gray-500 flex-shrink-0">
                            No tools found matching your search criteria.
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}

          {filteredGroups.length === 0 && (
            <div className="text-center py-12">
              <div className="mb-4">
                <Shield className="mx-auto h-12 w-12 text-gray-400" />
              </div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                {groups.length === 0 ? 'No toolkits available' : 'No results found'}
              </h3>
              <p className="text-gray-500">
                {groups.length === 0 
                  ? 'No pentest toolkits have been created yet. Check back later for updates.'
                  : 'Try adjusting your search criteria or filters.'
                }
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}; 